import React, { FC, useCallback, useEffect, useState } from "react";
import HomePageTitle from "@components/Home/HomePageTitle/HomePageTitle";
import { useTranslation } from "react-i18next";
import SessionList from "@components/SessionsList/SessionList";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useGetSessionsQuery } from "@store/sessionApi";
import { Order } from "@models/page-options";
import Loader from "@components/Loader/Loader";
import { Session } from "@models/session";
import SessionDrawer from "@components/Home/Sessions/SessionDrawer";
import { useNavigate, useParams } from "react-router-dom";
import PageWrapper from "@components/Home/PageWrapper";
import EmptyState from '@components/EmptyState/EmptyState';

const Sessions: FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [checkedSessions, setCheckedSessions] = useState<Set<Session>>(
    new Set(),
  );
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [selectedSession, setSelectedSession] = useState<Session | undefined>();
  const { t } = useTranslation();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: sessions, isLoading } = useGetSessionsQuery(
    {
      patientId: selectedPatientId!,
      order: Order.DESC,
      page: page,
      pageSize: pageSize,
    },
    {
      skip: !selectedPatientId,
    },
  );

  useEffect(() => {
    const id = params.id;
    if (id) {
      const sess = sessions?.data?.find((s) => s.id === id);
      if (sess) {
        setSelectedSession(sess);
      }
    }
  }, [params, sessions]);

  const onPageChange = useCallback((e: any, page: number) => {
    setPage(page + 1);
  }, []);

  const onRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageSize(parseInt(event.target.value, 10));
      setPage(1);
    },
    [],
  );

  const onEdit = useCallback(
    (session: Session) => {
      setSelectedSession(session);
      navigate(`/transcripts/${session.id}`);
    },
    [navigate],
  );

  const checkSession = useCallback((session: Session) => {
    setCheckedSessions((sessions: Set<Session>) => {
      const newSet = new Set(sessions);
      if (newSet.has(session)) {
        newSet.delete(session);
      } else {
        newSet.add(session);
      }
      return newSet;
    });
  }, []);

  const onClose = useCallback(() => {
    setSelectedSession(undefined);
    navigate("/transcripts");
  }, [navigate]);

  return (
    <PageWrapper>
      <HomePageTitle title={t("sessions.title")} />
      {!sessions?.data || isLoading ? (
        <Loader />
      ) : (
        sessions.data.length ? (
          <SessionList
            sessions={sessions}
            onEdit={onEdit}
            onSelect={checkSession}
            selectedSessions={checkedSessions}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
          />) : (
            <EmptyState image={`${process.env.PUBLIC_URL}/images/art-transcript.jpeg`} label={t('sessions.noData')}/>
          )
      )}
      <SessionDrawer session={selectedSession} onClose={() => onClose()} />
    </PageWrapper>
  );
};

export default Sessions;
